'use client';

import { ItemGrid } from '@chemist4u/newt-components';
import type { GA4Item } from '@lib/analytics/types';
import { useProductListing } from '@lib/hooks/use-product-listing';
import { useScrollObserver } from '@lib/hooks/use-scroll-observer';
import type { SortOptions } from '@lib/util/sort-products';
import { TrackItemList } from '@modules/analytics/components/trackers';
import { Spinner } from '@modules/common/icons';
import ProductPreview from '@modules/products/components/product-preview';
import { ProductGridEmptyState } from '@modules/store/templates/product-grid-empty-state';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductCardVM } from 'types/view-models';

const ProductListingToolbar = dynamic(
  () => import('@modules/store/components/product-listing-toolbar'),
);

type ProductListingProps = {
  countryCode: string;
  categoryId?: string;
  collectionId?: string;
  searchQuery?: string;
  listId?: string;
  listName?: string;
  defaultProducts: ProductCardVM[];
  defaultGa4Items: GA4Item[];
  defaultCount: number;
};

type ListingParams = {
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
};

const DEFAULT_LISTING_PARAMS: ListingParams = { sortBy: 'popular' };

// Deliberately does NOT use `useSearchParams()` — that hook forces Next.js to
// bail this component out of static/ISR rendering and paint it only after
// client JS hydrates (behind a Suspense fallback), so with JS disabled the
// product grid would never appear at all. Sort/price state instead starts at
// the default (rendering `defaultProducts` — identical to what the server
// fetched — on every render pass: server, first client paint, and post-hydration)
// and is corrected client-side, after mount, by reading the URL directly. That
// keeps the default view (the common case) visible without JS and free of any
// hydration swap/layout shift.
function readParamsFromLocation(): ListingParams {
  const params = new URLSearchParams(window.location.search);
  const sortBy = (params.get('sortBy') as SortOptions | null) ?? 'popular';
  const minPriceParam = params.get('minPrice');
  const maxPriceParam = params.get('maxPrice');
  return {
    sortBy,
    minPrice: minPriceParam ? Number(minPriceParam) : undefined,
    maxPrice: maxPriceParam ? Number(maxPriceParam) : undefined,
  };
}

export default function ProductListing({
  countryCode,
  categoryId,
  collectionId,
  searchQuery,
  listId,
  listName,
  defaultProducts,
  defaultGa4Items,
  defaultCount,
}: ProductListingProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [{ sortBy, minPrice, maxPrice }, setListingParams] =
    useState<ListingParams>(DEFAULT_LISTING_PARAMS);

  // Mirrors the latest listing params outside of React state so
  // `updateListingParams` never reads a stale value if it's called again
  // before the state update above has re-rendered this component (e.g. two
  // distinct filter controls triggered in the same tick).
  const listingParamsRef = useRef<ListingParams>(DEFAULT_LISTING_PARAMS);

  // Corrects state from the real URL once mounted (deep links to a non-default
  // sort/filter) and keeps it in sync with browser back/forward navigation.
  useEffect(() => {
    const params = readParamsFromLocation();
    listingParamsRef.current = params;
    setListingParams(params);
    const onPopState = () => {
      const next = readParamsFromLocation();
      listingParamsRef.current = next;
      setListingParams(next);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const updateListingParams = useCallback(
    (updates: Partial<ListingParams>) => {
      // Router navigation is a side effect and must not run inside a
      // `setState` updater — React can invoke that updater during its render
      // pass, and `router.push` there trips "Cannot update a component
      // (Router) while rendering a different component". Compute `next` from
      // `listingParamsRef` (kept in sync on every call/URL correction, unlike
      // a closure over `sortBy`/`minPrice`/`maxPrice`) instead, so this stays
      // a plain handler: one `setState` call, then the side effect.
      const next = { ...listingParamsRef.current, ...updates };
      listingParamsRef.current = next;
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('sortBy', next.sortBy);
      if (next.minPrice != null) {
        urlParams.set('minPrice', String(next.minPrice));
      } else {
        urlParams.delete('minPrice');
      }
      if (next.maxPrice != null) {
        urlParams.set('maxPrice', String(next.maxPrice));
      } else {
        urlParams.delete('maxPrice');
      }
      urlParams.set('page', '1');
      setListingParams(next);
      router.push(`${pathname}?${urlParams.toString()}`);
    },
    [pathname, router],
  );

  const handleSortChange = useCallback(
    (value: string) => updateListingParams({ sortBy: value as SortOptions }),
    [updateListingParams],
  );

  const handlePriceApply = useCallback(
    ({ min, max }: { min: number | null; max: number | null }) =>
      updateListingParams({
        minPrice: min ?? undefined,
        maxPrice: max ?? undefined,
      }),
    [updateListingParams],
  );

  const {
    products,
    ga4Items,
    count,
    isOverriding,
    hasMore,
    isPending,
    loadMoreRef,
  } = useProductListing({
    sortBy,
    minPrice,
    maxPrice,
    countryCode,
    categoryId,
    collectionId,
    searchQuery,
    listId,
    listName,
    defaultProducts,
    defaultGa4Items,
    defaultCount,
  });

  const sentinelRef = useScrollObserver(hasMore, loadMoreRef);

  return (
    <div data-testid="products-list">
      <TrackItemList items={ga4Items} listId={listId} listName={listName} />
      <ProductListingToolbar
        sortBy={sortBy}
        productCount={count}
        minPrice={minPrice}
        maxPrice={maxPrice}
        isLoading={isOverriding}
        onSortChange={handleSortChange}
        onPriceApply={handlePriceApply}
      />
      <ProductListingBody
        isOverriding={isOverriding}
        products={products}
        hasMore={hasMore}
        isPending={isPending}
        sentinelRef={sentinelRef}
      />
    </div>
  );
}

type ProductListingBodyProps = {
  isOverriding: boolean;
  products: ProductCardVM[];
  hasMore: boolean;
  isPending: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
};

function ProductListingBody({
  isOverriding,
  products,
  hasMore,
  isPending,
  sentinelRef,
}: ProductListingBodyProps) {
  if (isOverriding) {
    return (
      <div className="flex justify-center py-40" aria-live="polite">
        <Spinner
          size={48}
          className="text-fg-interactive-strong animate-spin motion-reduce:animate-none"
        />
        <span className="sr-only">Loading products…</span>
      </div>
    );
  }

  if (products.length === 0) {
    return <ProductGridEmptyState />;
  }

  return (
    <>
      <div className="animate-fade-in">
        <ItemGrid itemSize="default">
          {products.map((card) => (
            <ProductPreview key={card.id} card={card} />
          ))}
        </ItemGrid>
      </div>
      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-8">
          <div className="text-sm text-fg-neutral-soft">
            {isPending ? 'Loading more...' : ' '}
          </div>
        </div>
      )}
    </>
  );
}
